import { DESCRIPTIONS } from '@simOn/common/helpers';
import { UploadErrorType } from '@simOn/common/models';
import { LimitInformation } from '@simOn/ui/sim-data-usage';
import { bytesToMb } from '@simOn/utils/helpers';
import { SupportedCommonTypes, SupportedFileTypesInterface } from './supported-extensions.const';

/** Constraints a picked file is checked against before it is handed over to an upload. */
export interface UploadFileRules {
  /** File types the file may have; matched by its extension, case-insensitively. */
  supportedTypes: readonly SupportedFileTypesInterface<string>[];
  /** Maximum size of a single file in bytes. */
  maxSize: number;
  /** Storage budget of the space; skipped when unknown or unlimited. */
  blobLimits?: LimitInformation | null;
}

/** Resolves format labels (e.g. `['PNG', 'SVG']`) to the app-wide file type definitions; unknown labels are ignored. */
export function resolveSupportedTypes(labels: readonly string[]): SupportedFileTypesInterface<string>[] {
  return SupportedCommonTypes.filter(({ label }) => labels.includes(label));
}

/** Value for the `accept` attribute of a file input: the extensions and mime types of the given file types. */
export function toAcceptAttribute(types: readonly SupportedFileTypesInterface<string>[]): string {
  const extensions = types.map(({ fileExtension }) => fileExtension);
  const mimeTypes = Array.from(new Set(types.map(({ mimeType }) => mimeType)));
  return [...extensions, ...mimeTypes].join(',');
}

/** Lower-case extension of a file name including the dot (`'.svg'`); empty when the name has none. */
export function getFileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex > 0 ? fileName.slice(dotIndex).toLowerCase() : '';
}

/**
 * Validates a file against the rules and returns the first violated one as a ready-to-display message,
 * or `null` when the file may be uploaded. Order: format → single file size → space storage budget.
 */
export function validateUploadFile(file: File, rules: UploadFileRules): UploadErrorType | null {
  const extension = getFileExtension(file.name);
  if (!rules.supportedTypes.some(({ fileExtension }) => fileExtension === extension)) {
    return { message: DESCRIPTIONS.notSupportedExtension(file.name), type: 'error' };
  }

  if (file.size > rules.maxSize) {
    return { message: DESCRIPTIONS.sizeGreaterThen(file.name, bytesToMb(rules.maxSize)), type: 'error' };
  }

  const blobLimits = rules.blobLimits;
  if (blobLimits && !blobLimits.isUnlimited && blobLimits.used + file.size > blobLimits.limit) {
    return { message: DESCRIPTIONS.storageLimitExceeded(file.name), type: 'error' };
  }

  return null;
}
